import { firstValueFrom, Observable, of, ReplaySubject, Subscription, } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import {
  ECurrency, EShoppingCartGetQueryType, PriceDto, ProductIdentifierDto, ShoppingCartSetItemDto,
  ShoppingCartCreateByTemplateCommandResult, ShoppingCartDto,
  ShoppingCartGetQueryResult, ShoppingCartItemDto, ShoppingCartSaveAsTemplateCommandResult, ShoppingCartSetOrderDataCommand,
  ShoppingCartSetOrderDataCommandResult, ShoppingCartTemplateListGetQueryResult, ProductOrderPriceGetItemDto, ShoppingCartItemIdentifierDto,
  QuantityUnitDto, ShoppingCartValidationResultDto, ShoppingCartValidateCommandResult, SwaggerException, ShoppingCartValidationException,
  EShoppingCartItemValidationError, ProductStockInformationGetDto, EError,
} from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { ICartService, ICartServiceToken } from './cart-service.interface';
import { ServiceLocator } from '../../infrastructure/service-locator.service';
import { IShoppingCartApiService, IShoppingCartApiServiceToken } from '../../api/shopping-cart/shopping-cart-api-service.interface';
import { CartValidationHelperService } from '../../ui/cart-validation-helper.service';
import { IAuthService, IAuthServiceToken } from '../../infrastructure/authentication/auth-service.interface';
import { IProductService, IProductServiceToken } from '../product/product-service.interface';
import { forwardRef, inject, Injectable, Provider } from '@angular/core';
import { ErrorCodeService } from '../../infrastructure/error-code.service';
import { IComparisonService, IComparisonServiceToken } from '../../infrastructure/comparison/comparison-service.interface';
import { ILoggerService, ILoggerServiceToken } from '../../infrastructure/logger/logger-service.interface';
import { IInitializable, INITIALIZABLE_SERVICES } from 'shared/app-core/initializable.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Injectable({
  providedIn: null,
  deps: [
    ILoggerServiceToken,
    IShoppingCartApiServiceToken,
    IAuthServiceToken,
    IComparisonServiceToken,
    IProductServiceToken
  ]
})
export class CartService implements ICartService, IInitializable {
  private readonly toastService: IToastService = inject(IToastServiceToken);
  protected readonly logger: ILoggerService = inject(ILoggerServiceToken);
  protected readonly shoppingCartApiService: IShoppingCartApiService = inject(IShoppingCartApiServiceToken);
  protected readonly authService: IAuthService = ServiceLocator.injector.get(IAuthServiceToken);
  protected readonly comparisonService: IComparisonService = inject(IComparisonServiceToken);
  protected readonly productService: IProductService = inject(IProductServiceToken);

  public cartCurrent: ShoppingCartDto | undefined;
  protected cartValidationState: ShoppingCartValidationResultDto | undefined;

  private cartSource: ReplaySubject<ShoppingCartDto | undefined> = new ReplaySubject(1);
  private cartItemCountSource: ReplaySubject<number> = new ReplaySubject(1);
  private cartTemplateListSource: ReplaySubject<ShoppingCartDto[]> = new ReplaySubject(1);
  private cartValidationSource: ReplaySubject<ShoppingCartValidationResultDto | undefined> = new ReplaySubject(1);

  protected authenticationSubscription: Subscription | undefined;
  private cartSubscription: Subscription | undefined;

  public isLoggedIn?: boolean | undefined;
  private allowAnonymousAddToCart: boolean;
  protected shouldValidateCarts: boolean;
  protected shouldLoadPrices: boolean | undefined;

  private currency: ECurrency;

  public constructor() {
    this.cartTemplateListSource.next([]);
    this.allowAnonymousAddToCart = ENVIRONMENT.allowAnonymousAddToCart;
    this.shouldValidateCarts = ENVIRONMENT.cart.shouldValidateCarts;
    this.currency = ENVIRONMENT.price.defaultCurrency ?? ECurrency.Euro;
  }

  public init(): void | Promise<void> {
    this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();

    this.authenticationSubscriptionInit();
    this.cartSubscriptionInit();
  }

  protected authenticationSubscriptionInit() {
    this.logger.trace(`authenticationSubscriptionInit called.`, this.constructor.name);
    if (!this.authenticationSubscription) {
      this.authenticationSubscription = this.authService.authenticatedGet().subscribe({
        next: async (isAuthenticated: boolean) => {
          this.isLoggedIn = isAuthenticated;

          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();

          if (this.isLoggedIn) {
            this.cartTemplateListInitAsync();

            let latestCartMergedWithCartValidationResult
              : { cart: ShoppingCartDto | undefined, validationResult: ShoppingCartValidationResultDto | undefined }
              = await this.cartLatestGetMergedWithCartCurrentAsync(true);
            this.cartCurrentReplaceBy(latestCartMergedWithCartValidationResult.cart);
            this.cartValidationStateSet(latestCartMergedWithCartValidationResult.cart, latestCartMergedWithCartValidationResult.validationResult);
          } else {
            this.cartCurrentSetToGuestCart();
          }
        }
      });
    }
  }

  protected async cartTemplateListInitAsync() {
    this.logger.trace(`cartTemplateListInitAsync called.`, this.constructor.name);
    try {
      let result: ShoppingCartTemplateListGetQueryResult = await firstValueFrom(this.shoppingCartApiService.shoppingCartTemplateListGet());
      if (!result) {
        this.logger.info(`No data upon calling shoppingCartTemplateListGet received.`, this.constructor.name);
      }
      let cartTemplateList = result.shoppingCartTemplateList;
      if (!cartTemplateList) {
        cartTemplateList = [];
      }
      this.cartTemplateListSource.next(cartTemplateList);
    } catch (error) {
      this.logger.info(`${error}`, this.constructor.name);
      this.cartTemplateListSource.next([]);
    }
  }

  protected cartSubscriptionInit() {
    this.logger.trace(`cartSubscriptionInit called.`, this.constructor.name);
    if (!this.cartSubscription) {
      this.cartSubscription = this.cartCurrentChangedGet().subscribe(
        (cart) => {
          let itemListTemp = cart?.itemList;
          if (!itemListTemp) {
            itemListTemp = [];
          }
          this.cartItemCountSource.next(itemListTemp.length);
        }
      );
    }
  }

  protected shouldLoadPricesCurrentStateGet(): boolean | undefined {
    this.logger.trace(`shouldLoadPricesCurrentStateGet called.`, this.constructor.name);
    return ENVIRONMENT.price.allowAnonymousPrice || this.isLoggedIn;
  }

  public cartCurrentReplaceBy(cart: ShoppingCartDto | undefined) {
    this.logger.trace(`cartCurrentReplaceBy called.`, this.constructor.name);
    this.cartCurrent = cart;
    this.cartSource.next(this.cartCurrent);
  }

  public cartValidationStateSet(cart: ShoppingCartDto | undefined, cartValidationResult: ShoppingCartValidationResultDto | undefined) {
    this.logger.trace(`cartValidationStateSet called.`, this.constructor.name);
    if (cart != undefined) {
      let isCartParameterCurrentCart = this.cartCurrent?.cartNumber == cart.cartNumber;
      if (isCartParameterCurrentCart == false) {
        throw new Error("Missing implementation: Trying to store a cart validation 'not for the current cart'");
      }
    }
    this.cartValidationState = cartValidationResult;
    this.cartValidationSource.next(this.cartValidationState);
  }

  public cartCurrentSetToGuestCart() {
    this.logger.trace(`cartCurrentSetToGuestCart called.`, this.constructor.name);
    let guestCart = undefined;

    if (this.allowAnonymousAddToCart) {
      guestCart = new ShoppingCartDto();
    }

    this.cartCurrentReplaceBy(guestCart);
    this.cartValidationStateSet(undefined, undefined);
  }

  public async cartCurrentReplaceByApiAsync(cartGetQueryType: EShoppingCartGetQueryType, validateCart?: boolean | undefined) {
    this.logger.trace(`cartCurrentReplaceByApiAsync called.`, this.constructor.name);

    let shouldValidateNewCart = validateCart;
    if (shouldValidateNewCart == undefined) {
      shouldValidateNewCart = this.shouldValidateCarts;
    }

    await firstValueFrom(this.cartGet(cartGetQueryType, shouldValidateNewCart)).then((cartGetResult) => {
      this.cartCurrentReplaceBy(cartGetResult.shoppingCart);

      if (this.shouldValidateCarts == true) {
        this.cartValidationStateSet(undefined, cartGetResult.shoppingCartValidationResult);
      }
    }).catch((ex) => {
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Info,
        title: $localize`:@@ShoppingCartDataServiceReplaceByLatestErrorTitle:Shopping cart changed`,
        message: $localize`:@@ShoppingCartDataServiceReplaceByLatestErrorMessage:Please refresh the page.`,
        code: JSON.parse(ex.response).errorCode
      }));
      this.logger.error(ex, this.constructor.name);
      throw ex;
    });
  }

  public async cartMergeAsync(
    sourceCart: ShoppingCartDto | undefined,
    targetCart: ShoppingCartDto | undefined,
    updateTargetCartViaApi: boolean,
  ): Promise<ShoppingCartDto> {
    this.logger.trace(`cartMergeAsync called.`, this.constructor.name);
    if (targetCart == undefined) {
      targetCart = new ShoppingCartDto();
    }

    if (sourceCart == undefined) {
      return targetCart;
    }

    sourceCart.itemList?.forEach(
      (sourceCartItem) => {
        let targetCartItemIndex = this.cartItemIndexGetByIdentifier(sourceCartItem.productIdentifier, targetCart);
        if (targetCartItemIndex != undefined && targetCartItemIndex >= 0) {
          targetCart?.itemList?.splice(targetCartItemIndex, 1);
        }
        targetCart?.itemList?.push(sourceCartItem);
      }
    );

    if (updateTargetCartViaApi == false) {
      return targetCart;
    }

    let targetCartItemList: ShoppingCartSetItemDto[] = [];
    targetCart?.itemList?.forEach(
      (cartItem) => {
        targetCartItemList.push(new ShoppingCartSetItemDto({
          shoppingCartItemIdentifier: new ShoppingCartItemIdentifierDto({
            productIdentifier: cartItem.productIdentifier,
            orderUnit: cartItem.orderUnit?.quantityUnit,
          }),
          quantity: cartItem.quantity,
          deliveryInformation: cartItem.deliveryInformation,
        }));
      }
    );

    let deliveryDate = targetCart.deliveryDate ? new Date(targetCart.deliveryDate) : new Date();

    try {
      let setResult = await firstValueFrom(this.shoppingCartApiService.shoppingCartItemSet(targetCartItemList, targetCart.cartNumber, deliveryDate));
      if (setResult.shoppingCart != undefined) {
        return setResult.shoppingCart;
      }
      throw new Error("No result upon updating the itemList in the target cart via api after a merge with another cart.");
    } catch (error) {

      /*
        alternative ways
        if (SwaggerException.isSwaggerException(error)) {
          let swaggerException = <SwaggerException>error;
          let exception = swaggerException.result;
        }
       */

      if (error instanceof SwaggerException) {
        let swaggerException = error as SwaggerException;
        let exception = swaggerException.result;

        if (exception instanceof ShoppingCartValidationException) {
          let shoppingCartValidationException = exception as ShoppingCartValidationException;
          let validationResult = shoppingCartValidationException.shoppingCartValidationResult;
        }

        this.logger.error(exception, this.constructor.name);

      } else {
        this.logger.error(error, this.constructor.name);
      }

    }

    return targetCart;
  }

  public cartItemGetByIdentifier(
    productIdentifer: ProductIdentifierDto | undefined,
    cart?: ShoppingCartDto | undefined,
  ): ShoppingCartItemDto | undefined {
    this.logger.trace(`cartItemGetByIdentifier called.`, this.constructor.name);
    if (productIdentifer == undefined) {
      return undefined;
    }

    if (cart == undefined) {
      cart = this.cartCurrent;
    }

    let item = this.cartCurrent?.itemList?.find(
      (cartItem) => {
        return this.comparisonService.equalsProductIdentifierByNumberOrGuid(productIdentifer, cartItem.productIdentifier);
      }
    );
    return item;
  }

  public cartItemIndexGetByIdentifier(
    productIdentifer: ProductIdentifierDto | undefined,
    cart?: ShoppingCartDto | undefined,
  ): number | undefined {
    this.logger.trace(`cartItemIndexGetByIdentifier called.`, this.constructor.name);
    if (productIdentifer == undefined) {
      return undefined;
    }

    if (cart == undefined) {
      cart = this.cartCurrent;
    }

    let itemIndex = this.cartCurrent?.itemList?.findIndex(
      (cartItem) => {
        return this.comparisonService.equalsProductIdentifierByNumberOrGuid(productIdentifer, cartItem.productIdentifier);
      }
    );
    return itemIndex;
  }

  public async cartLatestGetMergedWithCartCurrentAsync(
    updateLatestCartViaApi: boolean,
  ): Promise<{ cart: ShoppingCartDto | undefined, validationResult: ShoppingCartValidationResultDto | undefined }> {
    this.logger.trace(`cartLatestGetMergedWithCartCurrentAsync called.`, this.constructor.name);
    let cartGetResult: ShoppingCartGetQueryResult = await firstValueFrom(this.cartGet(EShoppingCartGetQueryType.GetLatestOrCreateNew, this.shouldValidateCarts));
    let cartNew = cartGetResult.shoppingCart;
    if (cartNew == undefined) {
      return { cart: undefined, validationResult: undefined };
    }

    let mergedCart = await this.cartMergeAsync(this.cartCurrent, cartNew, updateLatestCartViaApi);
    return { cart: mergedCart, validationResult: cartGetResult.shoppingCartValidationResult };
  }

  public async cartSetOrderDataAsync(shoppingCartSetOrderDataCommand: ShoppingCartSetOrderDataCommand): Promise<ShoppingCartSetOrderDataCommandResult> {
    this.logger.trace(`cartSetOrderDataAsync called.`, this.constructor.name);
    return await firstValueFrom(this.shoppingCartApiService.shoppingCartSetOrderData(shoppingCartSetOrderDataCommand));
  }

  public cartGet(shoppingCartGetQueryType: EShoppingCartGetQueryType, validateCart: boolean, cartNumber?: number | undefined, useCancellationLogic: boolean | undefined = true): Observable<ShoppingCartGetQueryResult> {
    this.logger.trace(`cartGet called. shoppingCartGetQueryType(${shoppingCartGetQueryType}), validateCart(${validateCart}), cartNumber(${cartNumber})`, this.constructor.name);
    if (this.isLoggedIn) {
      try {
        return this.shoppingCartApiService.shoppingCartGet(shoppingCartGetQueryType, validateCart, cartNumber, useCancellationLogic);
      } catch (error) {
        this.logger.error(error, this.constructor.name);
        throw error;
      }
    } else {
      let shoppingCartGetQueryResult = new ShoppingCartGetQueryResult();

      let shoppingCart = new ShoppingCartDto();
      shoppingCart.itemList = [];

      shoppingCartGetQueryResult.shoppingCart = shoppingCart;

      return of(shoppingCartGetQueryResult);
    }
  }

  public cartCurrentChangedGet(): Observable<ShoppingCartDto | undefined> {
    this.logger.trace(`cartCurrentChangedGet called.`, this.constructor.name);
    return this.cartSource.asObservable();
  }

  public cartCurrentItemCountChangedGet(): Observable<number> {
    this.logger.trace(`cartCurrentItemCountChangedGet called.`, this.constructor.name);
    return this.cartItemCountSource.asObservable();
  }

  public cartTemplateListChangedGet(): Observable<ShoppingCartDto[]> {
    this.logger.trace(`cartTemplateListChangedGet called.`, this.constructor.name);
    return this.cartTemplateListSource.asObservable();
  }

  public cartValidationChangedGet(): Observable<ShoppingCartValidationResultDto | undefined> {
    this.logger.trace(`cartValidationChangedGet called.`, this.constructor.name);
    return this.cartValidationSource.asObservable();
  }

  public async cartCurrentInitAsync(cartGetQueryType: EShoppingCartGetQueryType) {
    this.logger.trace(`cartCurrentInitAsync called. cartGetQueryType(${cartGetQueryType})`, this.constructor.name);
    if (!this.isLoggedIn) {
      this.cartCurrentSetToGuestCart();
      return;
    } else {
      await this.cartCurrentReplaceByApiAsync(cartGetQueryType);
    }
  }

  public async cartItemSetByProductIdentifierAsync(
    cart: ShoppingCartDto | undefined,
    productIdentifier: ProductIdentifierDto,
    quantity: number = 1,
    orderUnit?: QuantityUnitDto | undefined,
    deliveryInformation?: string
  ): Promise<ShoppingCartDto | undefined> {
    this.logger.trace(`cartCurrentInitAsync called.`, this.constructor.name);
    try {
      if (cart == undefined) {
        cart = this.cartCurrent;
      }

      let shoppingCartItemIdentifier = new ShoppingCartItemIdentifierDto({
        productIdentifier: productIdentifier
      });
      if (orderUnit != undefined) {
        shoppingCartItemIdentifier.orderUnit = orderUnit.quantityUnit;
      }
      let shoppingCartSetItem = new ShoppingCartSetItemDto({
        shoppingCartItemIdentifier: shoppingCartItemIdentifier,
        quantity: quantity,
        deliveryInformation: deliveryInformation,
      });
      let result = await this.cartItemListSetAsync(cart, [shoppingCartSetItem]);

      if (!this.isLoggedIn) {
        let resultCartItem = result?.itemList?.find(i => this.comparisonService.equalsProductIdentifierByNumberOrGuid(i.productIdentifier, productIdentifier));
        if (resultCartItem != undefined && orderUnit != undefined) {
          resultCartItem.orderUnit = orderUnit;
        }
      }

      let updatedCartIsCurrentCart = this.cartCurrent?.cartNumber == cart?.cartNumber;
      if (updatedCartIsCurrentCart && result != undefined) {
        this.cartCurrentReplaceBy(result);
      }

      return result;
    } catch (error) {

      /*
        alternative ways
        if (SwaggerException.isSwaggerException(error)) {
          let swaggerException = <SwaggerException>error;
          let exception = swaggerException.result;
        }
       */
      if (error instanceof SwaggerException) {
        let swaggerException = error as SwaggerException;
        let exception = swaggerException.result;

        if (exception instanceof ShoppingCartValidationException) {
          let shoppingCartValidationException = exception as ShoppingCartValidationException;
          let validationResult = shoppingCartValidationException.shoppingCartValidationResult;

          let hasItemValidationError = validationResult?.itemValidationErrorList != undefined && validationResult?.itemValidationErrorList.length > 0;

          let toastService = ServiceLocator.injector.get(IToastServiceToken);

          if (hasItemValidationError) {

            let message = $localize`:@@CartDataServiceCartItemSetValidationErrorMessage:Cart could not be updated.`;
            message += '<br><br>';

            let isProductOutOfStock = validationResult?.itemValidationErrorList?.find(e => e.shoppingCartItemValidationError === EShoppingCartItemValidationError.ProductIsOutOfStock);
            if (isProductOutOfStock) {
              let productStockInformationGetItem = new ProductStockInformationGetDto({
                productIdentifier: productIdentifier,
                quantityUnit: orderUnit?.quantityUnit
              });
              let result = await firstValueFrom(this.productService.productStockInformationGet([productStockInformationGetItem]));

              let productStockInformationList = result.productStockInformationList;

              let productStockInformation = productStockInformationList?.find(i => this.comparisonService.equalsProductIdentifierByNumberOrGuid(i.productIdentifier, productIdentifier));
              if (productStockInformation) {

                let isStockBelowOrEqual0 = productStockInformation.availableQuantity <= 0;

                if (isStockBelowOrEqual0) {
                  let stockAvailableBelow0Prefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Prefix:There are no products `;
                  let stockAvailableBelow0Suffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Suffix: in stock.`;
                  message += `${stockAvailableBelow0Prefix}(${productStockInformation.quantityUnit?.quantityUnitCaption})${stockAvailableBelow0Suffix}`;
                  message += '<br>';
                } else {
                  let stockAvailableQuantityPrefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantityPrefix:It is possible to order only a quantity of `;
                  let stockAvailableQuantitySuffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantitySuffix:.`;
                  message += `${stockAvailableQuantityPrefix}${productStockInformation.availableQuantity} ${productStockInformation.quantityUnit?.quantityUnitCaption}${stockAvailableQuantitySuffix}`;
                  message += '<br>';
                }
              }
            }

            validationResult?.itemValidationErrorList?.filter(
              (e) => e.shoppingCartItemValidationError !== EShoppingCartItemValidationError.ProductIsOutOfStock)
              .forEach(
                (itemError) => {
                  let errorExplanation = CartValidationHelperService.shoppingCartItemValidationErrorExplanationGet(itemError.shoppingCartItemValidationError);
                  message += `- Product #${itemError.productIdentifier?.productNumber}: ${errorExplanation}`;
                  message += '<br>';
                }
              );

            toastService.toastItemAdd(new ToastItem({
              type: EToastItemType.Danger,
              title: $localize`:@@CartDataServiceCartItemSetValidationErrorTitle:Cart update failed`,
              message: message,
              code: ErrorCodeService.formatErrorCode(EError.F170060),
            }));
          }
        }

        this.logger.error(exception, this.constructor.name);

      } else {
        this.logger.error(error, this.constructor.name);
      }

      return undefined;
    }
  }

  public cartSaveAsTemplate(cartNumber: number, targetCartName: string): Observable<boolean> {
    this.logger.trace(`cartSaveAsTemplate called. cartNumber(${cartNumber}), targetCartName(${targetCartName})`, this.constructor.name);
    if (!this.isLoggedIn) {
      return of(false);
    }

    return this.shoppingCartApiService.shoppingCartSaveAsTemplate(cartNumber, targetCartName).pipe(mergeMap(
      (shoppingCartSaveAsTemplateCommandResult: ShoppingCartSaveAsTemplateCommandResult) => {
        if (!shoppingCartSaveAsTemplateCommandResult) {
          this.logger.info(`No data upon calling ShoppingCartSaveAsTemplate received.`, this.constructor.name);
          return of(false);
        }
        let resultShoppingCart = shoppingCartSaveAsTemplateCommandResult.shoppingCart;
        if (!resultShoppingCart) {
          this.logger.info(`No new shopping cart after calling ShoppingCartSaveAsTemplate received.`, this.constructor.name);
          return of(false);
        }
        this.cartCurrentReplaceBy(resultShoppingCart);

        return this.shoppingCartApiService.shoppingCartTemplateListGet().pipe(mergeMap(
          (shoppingCartTemplateListGetQueryResult: ShoppingCartTemplateListGetQueryResult) => {
            if (!shoppingCartTemplateListGetQueryResult) {
              this.logger.info(`No data upon calling shoppingCartTemplateListGet received.`, this.constructor.name);
              return of(false);
            }
            let templateListTemp = shoppingCartTemplateListGetQueryResult.shoppingCartTemplateList;
            if (!templateListTemp) {
              templateListTemp = [];
            }
            this.cartTemplateListSource.next(templateListTemp);
            return of(true);
          }
        )).pipe(catchError(
          (er) => {
            this.logger.error(`${er}`, this.constructor.name);
            this.cartTemplateListSource.next([]);
            return of(false);
          }
        ));
      }
    )).pipe(catchError(
      (er) => {
        this.logger.error(`${er}`, this.constructor.name);
        return of(false);
      }
    ));
  }

  public cartCreateByTemplate(cartNumber: number): Observable<ShoppingCartDto> {
    this.logger.trace(`cartCreateByTemplate called. cartNumber(${cartNumber})`, this.constructor.name);
    if (!this.isLoggedIn) {
      throw new Error('Attempt to create a template by card as guest.');
    }

    return this.shoppingCartApiService.shoppingCartCreateByTemplate(cartNumber).pipe(
      mergeMap(
        (shoppingCartCreateByTemplateCommandResult: ShoppingCartCreateByTemplateCommandResult) => {
          let resultShoppingCart = shoppingCartCreateByTemplateCommandResult.shoppingCart;
          if (!resultShoppingCart) {
            throw new Error('No shopping cart within the shopping cart create by template command.');
          }

          this.cartCurrentReplaceBy(resultShoppingCart);

          return of(resultShoppingCart);
        }
      )
    ).pipe(
      catchError(
        (er) => {
          this.logger.error(`${er}`, this.constructor.name);
          throw new Error('Failure upon creating a new shopping cart by template.');
        }
      )
    );
  }

  public cartTemplateAdd(templateName: string, itemList?: ShoppingCartSetItemDto[] | undefined) {
    this.logger.trace(`cartTemplateAdd called. templateName(${templateName})`, this.constructor.name);
    if (!this.isLoggedIn) {
      throw new Error('Attempt to create a template as guest.');
    }

    return this.shoppingCartApiService.shoppingCartTemplateAdd(templateName, itemList);
  }

  public cartDeleteByCartNumber(cartNumber: number): Observable<boolean> {
    if (!this.isLoggedIn) {
      return of(false);
    }

    return this.shoppingCartApiService.shoppingCartDelete(cartNumber)
      .pipe(
        mergeMap(
          () => {
            return of(true);
          }
        )
      ).pipe(
        catchError(
          (er) => {
            this.logger.error(`${er}`, this.constructor.name);
            return of(false);
          }
        )
      );
  }

  public async cartTemplateListUpdateAsync(): Promise<void> {
    this.logger.trace(`cartTemplateListUpdateAsync called.`, this.constructor.name);
    try {
      let result = await firstValueFrom(this.shoppingCartApiService.shoppingCartTemplateListGet());
      let cartList = result.shoppingCartTemplateList;
      if (cartList == undefined) {
        this.cartTemplateListSource.next([]);
      } else {
        this.cartTemplateListSource.next(cartList);
      }
    } catch (error) {
      this.logger.error(error, this.constructor.name);
    }
  }

  public async cartItemListDeleteAsync(
    productIdentifierList: ProductIdentifierDto[],
    cartNumber: number | undefined = this.cartCurrent?.cartNumber,
    validateCartAfterwards: boolean | undefined = false,
  ): Promise<ShoppingCartDto | undefined> {
    this.logger.trace(`cartItemListDeleteAsync called.`, this.constructor.name);

    if (productIdentifierList.length <= 0) {
      return undefined;
    }

    let shouldUpdateCurrentCart = cartNumber == undefined || (this.cartCurrent != undefined && cartNumber == this.cartCurrent.cartNumber);

    if (!this.isLoggedIn) {
      this.shoppingCartItemListDeleteLocal(productIdentifierList);

      if (shouldUpdateCurrentCart) {
        this.cartSource.next(this.cartCurrent);
      }

      return this.cartCurrent;
    }

    let itemList = productIdentifierList.map(p => new ShoppingCartItemIdentifierDto({
      productIdentifier: p
    }));

    if (cartNumber == undefined) {
      return undefined;
    }

    let shoppingCartItemDeleteCommandResult = await firstValueFrom(this.shoppingCartApiService.shoppingCartItemDelete(itemList, cartNumber, validateCartAfterwards));

    let cart = shoppingCartItemDeleteCommandResult.shoppingCart;
    let cartValidationResult = shoppingCartItemDeleteCommandResult.shoppingCartValidationResult;

    if (!cart) {
      return undefined;
    }

    if (shouldUpdateCurrentCart) {
      this.cartCurrentReplaceBy(cart);
      this.cartValidationStateSet(cart, cartValidationResult);
    }

    return cart;
  }

  private shoppingCartItemListDeleteLocal(productItemIdifierList: ProductIdentifierDto[]) {
    this.logger.trace(`shoppingCartItemListDeleteLocal called.`, this.constructor.name);
    if (this.cartCurrent == undefined) {
      return;
    }

    let itemlistCopy = this.cartCurrent.itemList;
    if (itemlistCopy == undefined) {
      this.cartCurrent.itemList = [];
      return;
    }

    productItemIdifierList.forEach(
      (cartItemIdentifier: ProductIdentifierDto) => {
        if (!itemlistCopy) {
          return;
        }

        let matchingEntry = this.shoppingCartItemDtoGetByProductIdentifier(cartItemIdentifier, itemlistCopy);
        if (!matchingEntry) {
          return;
        }

        let index: number = itemlistCopy.indexOf(matchingEntry);
        if (index > -1) {
          itemlistCopy.splice(index, 1);
        }
      }
    );

    this.shoppingCartTotalPriceCalculate(this.cartCurrent);
    this.cartCurrent.itemList = itemlistCopy;
  }

  public async cartItemListSetAsync(
    shoppingCart: ShoppingCartDto | undefined,
    shoppingCartSetItemList: ShoppingCartSetItemDto[],
  ): Promise<ShoppingCartDto | undefined> {
    this.logger.trace(`cartItemListSetAsync called.`, this.constructor.name);
    if (shoppingCart == undefined) {
      shoppingCart = this.cartCurrent;
    }

    let updatedCartIsCurrentCart = this.cartCurrent?.cartNumber == shoppingCart?.cartNumber;

    if (shoppingCart == undefined) {
      throw new Error('No current shoppping cart given upon adding / updating items.');
    }
    if (shoppingCartSetItemList.length <= 0) {
      return undefined;
    }

    if (!this.isLoggedIn) {
      let shoppingCartItemList: ShoppingCartItemDto[] = [];
      let sequence: number = 0;

      shoppingCartSetItemList.forEach(
        (shoppingCartSetItem: ShoppingCartSetItemDto) => {
          if (shoppingCart == undefined) {
            return;
          }
          if (!shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier) {
            return;
          }

          sequence += 1;

          if (!shoppingCart.itemList) {
            shoppingCart.itemList = [];
          }

          let shoppingCartItem = this.shoppingCartItemDtoGetByProductIdentifier(shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier, shoppingCart.itemList);
          if (!shoppingCartItem) {
            shoppingCartItem = new ShoppingCartItemDto({
              productIdentifier: shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier,
              quantity: shoppingCartSetItem.quantity ? shoppingCartSetItem.quantity : 1,
              totalWeightInKg: 0,
              deliveryInformation: shoppingCartSetItem.deliveryInformation,
              unitPrice: undefined,
              totalPrice: undefined,
              sequence: sequence.toString(),
              isDutyProduct: undefined,
              orderUnit: new QuantityUnitDto({
                isDefault: false,
                isSmallest: false,
                quantityUnit: shoppingCartSetItem.shoppingCartItemIdentifier.orderUnit,
              })
            });

            shoppingCart.itemList.push(shoppingCartItem);
          }

          shoppingCartItem.quantity = shoppingCartSetItem.quantity ? shoppingCartSetItem.quantity : 1;
          shoppingCartItem.deliveryInformation = shoppingCartSetItem.deliveryInformation;

          shoppingCartItemList.push(shoppingCartItem);
        }
      );

      if (updatedCartIsCurrentCart == true) {
        await this.shoppingCartItemDetailLoadAsync(shoppingCartItemList);

        if (this.shouldLoadPrices) {
          await this.shoppingCartItemPriceLoadAsync(shoppingCartItemList);
        }
      }

      return this.cartCurrent;
    }

    if (!shoppingCart.cartNumber || shoppingCart.cartNumber <= 0) {
      throw new Error('Invalid cartNumber.');
    }

    let deliveryDate = shoppingCart.deliveryDate ? new Date(shoppingCart.deliveryDate) : new Date();

    try {
      let shoppingCartItemSetCommandResult = await firstValueFrom(this.shoppingCartApiService.shoppingCartItemSet(shoppingCartSetItemList, shoppingCart.cartNumber, deliveryDate));
      let shoppingCartResult = shoppingCartItemSetCommandResult.shoppingCart;

      if (!shoppingCartResult) {
        return undefined;
      }

      return shoppingCartResult;
    } catch (error) {

      /*
        alternative ways
        if (SwaggerException.isSwaggerException(error)) {
          let swaggerException = <SwaggerException>error;
          let exception = swaggerException.result;
        }
      */
      if (error instanceof SwaggerException) {
        let swaggerException = error as SwaggerException;
        let exception = swaggerException.result;

        if (exception instanceof ShoppingCartValidationException) {
          let shoppingCartValidationException = exception as ShoppingCartValidationException;
          let validationResult = shoppingCartValidationException.shoppingCartValidationResult;

          let hasItemValidationError = validationResult?.itemValidationErrorList != undefined && validationResult?.itemValidationErrorList.length > 0;

          let toastService = ServiceLocator.injector.get(IToastServiceToken);

          if (hasItemValidationError) {

            let message = $localize`:@@CartDataServiceCartItemSetValidationErrorMessage:Cart could not be updated.`;
            message += '<br><br>';

            for await (let shoppingCartSetItem of shoppingCartSetItemList) {
              let isProductOutOfStock = validationResult?.itemValidationErrorList?.find(e => e.shoppingCartItemValidationError === EShoppingCartItemValidationError.ProductIsOutOfStock);
              if (isProductOutOfStock) {
                let productStockInformationGetItem = new ProductStockInformationGetDto({
                  productIdentifier: shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier,
                  quantityUnit: shoppingCartSetItem.shoppingCartItemIdentifier?.orderUnit
                });
                let result = await firstValueFrom(this.productService.productStockInformationGet([productStockInformationGetItem]));

                let productStockInformationList = result.productStockInformationList;

                let productStockInformation = productStockInformationList?.find(i => this.comparisonService.equalsProductIdentifierByNumberOrGuid(i.productIdentifier, shoppingCartSetItem.shoppingCartItemIdentifier?.productIdentifier));
                if (productStockInformation) {

                  let isStockBelowOrEqual0 = productStockInformation.availableQuantity <= 0;

                  if (isStockBelowOrEqual0) {
                    let stockAvailableBelow0Prefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Prefix:There are no products `;
                    let stockAvailableBelow0Suffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableBelow0Suffix: in stock.`;
                    message += `${stockAvailableBelow0Prefix}(${productStockInformation.quantityUnit?.quantityUnitCaption})${stockAvailableBelow0Suffix}`;
                    message += '<br>';
                  } else {
                    let stockAvailableQuantityPrefix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantityPrefix:It is possible to order only a quantity of `;
                    let stockAvailableQuantitySuffix = $localize`:@@CartDataServiceCartItemSetStockInformationAvailableQuantitySuffix:.`;
                    message += `${stockAvailableQuantityPrefix}${productStockInformation.availableQuantity} (${productStockInformation.quantityUnit?.quantityUnitCaption})${stockAvailableQuantitySuffix}`;
                    message += '<br>';
                  }
                }
              }

              validationResult?.itemValidationErrorList?.filter(
                (e) => e.shoppingCartItemValidationError !== EShoppingCartItemValidationError.ProductIsOutOfStock)
                .forEach(
                  (itemError) => {
                    let errorExplanation = CartValidationHelperService.shoppingCartItemValidationErrorExplanationGet(itemError.shoppingCartItemValidationError);
                    message += `- Product #${itemError.productIdentifier?.productNumber}: ${errorExplanation}`;
                    message += '<br>';
                  }
                );
            }

            toastService.toastItemAdd(new ToastItem({
              type: EToastItemType.Danger,
              title: $localize`:@@CartDataServiceCartItemSetValidationErrorTitle:Cart update failed`,
              message: message,
              code: ErrorCodeService.formatErrorCode(EError.F170070),
            }));
          }
        }

        this.logger.error(exception, this.constructor.name);

      } else {
        this.logger.error(error, this.constructor.name);
      }

      return undefined;
    }
  }

  protected async shoppingCartItemDetailLoadAsync(shoppingCartItemList: ShoppingCartItemDto[]) {
    this.logger.trace(`shoppingCartItemDetailLoadAsync called.`, this.constructor.name);
    let productIdentifierList: ProductIdentifierDto[] = [];

    shoppingCartItemList.forEach(
      (shoppingCartItem: ShoppingCartItemDto) => {
        if (shoppingCartItem.productIdentifier) {
          productIdentifierList.push(shoppingCartItem.productIdentifier);
        }
      }
    );

    try {
      let result = await firstValueFrom(this.productService.productDetailGet(
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        undefined,
        productIdentifierList,
      ));
      let productDetailList = result.productList;
      if (!productDetailList) {
        return;
      }

      productDetailList.forEach(
        (productDetail) => {
          let matchingShoppingCartItem = this.shoppingCartItemDtoGetByProductIdentifier(productDetail.productIdentifier, this.cartCurrent?.itemList);

          if (!matchingShoppingCartItem) {
            return;
          }

          let weightInKg = productDetail.weightInKg;
          if (weightInKg == undefined) {
            weightInKg = 0;
          }

          let totalWeight = weightInKg * matchingShoppingCartItem.quantity;
          matchingShoppingCartItem.totalWeightInKg = totalWeight;
        }
      );

      this.shoppingCartTotalWeightCalculate(this.cartCurrent);
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }

  }

  protected async shoppingCartItemPriceLoadAsync(shoppingCartItemList: ShoppingCartItemDto[]) {
    this.logger.trace(`shoppingCartItemPriceLoadAsync called.`, this.constructor.name);
    let productList: ProductOrderPriceGetItemDto[] = [];

    shoppingCartItemList.forEach(
      (shoppingCartItem: ShoppingCartItemDto) => {
        if (shoppingCartItem.productIdentifier) {

          let productPriceGetItem = new ProductOrderPriceGetItemDto();
          productPriceGetItem.productIdentifier = shoppingCartItem.productIdentifier;
          productPriceGetItem.quantity = shoppingCartItem.quantity;
          productPriceGetItem.orderUnit = shoppingCartItem.orderUnit?.quantityUnit;

          productList.push(productPriceGetItem);
        }
      }
    );

    try {
      let result = await firstValueFrom(this.productService.productOrderPriceGet(productList));
      let productPriceList = result.productList;
      if (!productPriceList) {
        return;
      }

      productPriceList.forEach(
        (productPrice) => {
          let matchingShoppingCartItem = this.shoppingCartItemDtoGetByProductIdentifier(productPrice.productIdentifier, this.cartCurrent?.itemList);

          if (!matchingShoppingCartItem) {
            return;
          }

          matchingShoppingCartItem.unitPrice = productPrice.unitPrice;
          matchingShoppingCartItem.totalPrice = productPrice.totalPrice;
        }
      );

      this.shoppingCartTotalPriceCalculate(this.cartCurrent);
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }

  }

  private shoppingCartTotalWeightCalculate(cart: ShoppingCartDto | undefined) {
    this.logger.trace(`shoppingCartTotalWeightCalculate called.`, this.constructor.name);
    if (cart == undefined) {
      return;
    }

    let totalWeightInKg = 0;

    cart.itemList?.forEach(
      (shoppingCartItem) => {
        totalWeightInKg += shoppingCartItem.totalWeightInKg;
      }
    );

    cart.totalWeightInKg = totalWeightInKg;
  }

  private shoppingCartTotalPriceCalculate(shoppingCart: ShoppingCartDto | undefined) {
    this.logger.trace(`shoppingCartTotalPriceCalculate called.`, this.constructor.name);
    if (shoppingCart == undefined) {
      return;
    }

    let totalPrice = new PriceDto();
    totalPrice.currency = this.currency;
    let brutPrice = 0;
    let netPrice = 0;

    shoppingCart?.itemList?.forEach(
      (shoppingCartItem) => {
        let itemBrutPrice = shoppingCartItem.totalPrice?.priceWithDiscounts?.brutPrice;
        if (itemBrutPrice) {
          brutPrice += itemBrutPrice;
        }
        let itemNetPrice = shoppingCartItem.totalPrice?.priceWithDiscounts?.netPrice;
        if (itemNetPrice) {
          netPrice += itemNetPrice;
        }
      }
    );

    totalPrice.brutPrice = brutPrice;
    totalPrice.netPrice = netPrice;

    shoppingCart.totalPrice = totalPrice;
  }

  protected shoppingCartItemDtoGetByProductIdentifier(
    lookupProductIdentifier: ProductIdentifierDto | undefined,
    shoppingCartItemList: ShoppingCartItemDto[] | undefined,
  ): ShoppingCartItemDto | undefined {
    this.logger.trace(`shoppingCartItemDtoGetByProductIdentifier called.`, this.constructor.name);
    if (!shoppingCartItemList) {
      throw new Error('No list for shopping cart items while looking for existing entries');
    }

    if (!lookupProductIdentifier) {
      throw new Error('Missing productIdentifier to look for.');
    }

    let item = shoppingCartItemList.find(
      (existingShoppingCartItem: ShoppingCartItemDto) =>
        this.comparisonService.equalsProductIdentifierByNumberOrGuid(existingShoppingCartItem.productIdentifier, lookupProductIdentifier)
    );

    return item;
  }

  public cartValidate(cartNumber: number): Observable<ShoppingCartValidateCommandResult> {
    this.logger.trace(`cartValidate called. cartNumber(${cartNumber})`, this.constructor.name);
    return this.shoppingCartApiService.shoppingCartValidate(cartNumber);
  }

  public cartItemListValidationDelete(productIdentifierList: ProductIdentifierDto[]): void {
    this.logger.trace(`cartItemListValidationDelete called.`, this.constructor.name);
    productIdentifierList.forEach(
      (productIdentifier) => {
        let index = this.cartValidationState?.itemValidationErrorList?.findIndex(
          (v) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(v.productIdentifier, productIdentifier)
        );

        if (index != undefined && index >= 0) {
          this.cartValidationState?.itemValidationErrorList?.splice(index, 1);
        }
      }
    );

    this.cartEvaluationStateUpdate();
  }

  public cartEvaluationStateUpdate(): void {
    this.logger.trace(`cartEvaluationStateUpdate called.`, this.constructor.name);
    let cartValidationState: ShoppingCartValidationResultDto | undefined = JSON.parse(JSON.stringify(this.cartValidationState));
    if (cartValidationState == undefined) {
      return;
    }

    let cartHasInvalidItems = cartValidationState.itemValidationErrorList != undefined && cartValidationState.itemValidationErrorList?.length > 0;
    let cartHasErrors = cartValidationState.cartErrorList != undefined && cartValidationState.cartErrorList.length > 0;
    let cartHasValidationErrors = cartHasInvalidItems || cartHasErrors;

    if (this.cartValidationState != undefined) {
      cartValidationState.isValid = !cartHasValidationErrors;
    }

    this.cartValidationStateSet(undefined, cartValidationState);
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const CartServiceProvider: Provider[] = [
  {
    provide: ICartServiceToken,
    useClass: forwardRef(() => CartService),
    deps: [
      ILoggerServiceToken,
      IShoppingCartApiServiceToken,
      IAuthServiceToken,
      IComparisonServiceToken,
      IProductServiceToken
    ]
  },
  {
    provide: INITIALIZABLE_SERVICES,
    useExisting: ICartServiceToken,
    multi: true,
  },
];

